import React, { useState, useEffect } from 'react';
import Sparkle from 'react-sparkle';
import './BadgePreviewModal.css';

const BadgePreviewModal = ({ isOpen, onClose, gameId, onNavigateToLeaderboard }) => {
  const [badgeData, setBadgeData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && gameId) {
      fetchBadgeData();
    }
  }, [isOpen, gameId]);

  const fetchBadgeData = async () => {
    if (!gameId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/badge/${gameId}`);
      
      if (!response.ok) {
        throw new Error('Failed to load badge data');
      }

      const data = await response.json();
      setBadgeData(data.badge);
    } catch (err) {
      console.error('Error fetching badge data:', err);
      setError('Failed to load badge data');
    } finally {
      setIsLoading(false);
    }
  };

  const getPlacementText = (placement) => {
    if (placement === 1) return '1st place';
    if (placement === 2) return '2nd place';
    if (placement === 3) return '3rd place';
    return `#${placement} place`;
  };

  const shareBadge = async () => {
    if (!badgeData) return;

    const shareText = `🏆 Check out my Memory Game achievement! 

${badgeData.playerName} completed the game in ${badgeData.score} and achieved ${getPlacementText(badgeData.placement)}!

Can you beat my score?`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${badgeData.playerName}'s Memory Game Achievement`,
          text: shareText,
          url: badgeData.shareUrl
        });
      } catch (err) {
        console.log('Native share cancelled or failed:', err);
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = async () => {
    if (!badgeData) return;

    const shareText = `🏆 Check out my Memory Game achievement! 

${badgeData.playerName} completed the game in ${badgeData.score} and achieved ${getPlacementText(badgeData.placement)}!

Can you beat my score? Play now: ${badgeData.shareUrl}`;

    try {
      await navigator.clipboard.writeText(shareText);
      alert('Badge link copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Badge link copied to clipboard!');
    }
  };


  const handleClose = () => {
    onClose();
    // Navigate to leaderboard after closing
    if (onNavigateToLeaderboard) {
      onNavigateToLeaderboard();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="badge-preview-overlay">
      <div className="badge-preview-modal">
        <button 
          className="close-btn"
          onClick={handleClose}
          aria-label="Close badge preview"
        >
          ✕
        </button>
        
        <div className="badge-preview-content">
          <div className="badge-preview-header">
            <h2 className="badge-preview-title">🏆 Achievement Badge</h2>
          </div>
          
          <div className="game-name">
            <h3>NicoleCodeGirl Memory Game</h3>
          </div>
          
          {isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading your badge...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <h3>❌ Error</h3>
              <p>{error}</p>
              <button className="retry-btn" onClick={fetchBadgeData}>
                🔄 Try Again
              </button>
            </div>
          ) : badgeData ? (
            <>
              <div className="badge-preview-container">
                <Sparkle
                  color="pink"
                  count={12}
                  minSize={2}
                  maxSize={6}
                  overflowPx={8}
                  fadeOutSpeed={10}
                  flicker={false}
                />
                <Sparkle
                  color="#00ff00"
                  count={8}
                  minSize={1}
                  maxSize={4}
                  overflowPx={12}
                  fadeOutSpeed={15}
                  flicker={true}
                />
                <Sparkle
                  color="#ffd700"
                  count={6}
                  minSize={3}
                  maxSize={8}
                  overflowPx={5}
                  fadeOutSpeed={8}
                  flicker={false}
                />
                <div className="badge-info">
                  <h3 className="player-name">{badgeData.playerName}</h3>
                  <p className="score-info">Completed in {badgeData.score}</p>
                  <p className="placement-info">{getPlacementText(badgeData.placement)}</p>
                  <p className="theme-info">{badgeData.theme}</p>
                </div>
              </div>
              
              <div className="badge-actions">
                <button 
                  className="action-btn primary"
                  onClick={shareBadge}
                >
                  📤 Share Badge
                </button>
                
                <button 
                  className="action-btn secondary"
                  onClick={copyToClipboard}
                >
                  📋 Copy Link
                </button>
              </div>
            </>
          ) : (
            <div className="no-badge-state">
              <h3>🎮 No Badge Available</h3>
              <p>Complete a game to generate your achievement badge!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BadgePreviewModal;
